/**
 * recommendedPrompt · /init recommended_prompts 类型 + icon_type → AntD icon 映射
 *
 * 设计目标：
 *  · 后端给 EmptyState（建夹界面欢迎页）与 AISidebar QuickPrompts（AI 助手欢迎页）
 *    一份统一的「推荐分类项」，包含点击发送的 text、卡片副标题、语义 icon_type。
 *  · 前端做 icon_type → AntD icon 的最后一公里映射；未知 icon_type 不阻塞渲染，
 *    一律 fallback 到 `tag`，让 UI 始终能出，后续后端新增 type 前端补这里即可。
 *
 * 关联：
 *  · 数据源：src/pages/AIClassifyFolder/services/aiChatInit.ts → recommended_prompts
 *  · 解析：  src/pages/AIClassifyFolder/services/conversationRestore.ts → prewarmTeam
 *  · 渲染：  src/pages/AIClassifyFolder/components/PromptCardGrid（EmptyState / QuickPrompts 共用）
 */

import type { ComponentType } from 'react'
import { CalendarOutlined, UserOutlined, TagOutlined } from '@ant-design/icons'

/** iconType 映射到的 @ant-design/icons 组件形态 */
type AntdIconComponent = ComponentType<{ className?: string }>

/** /init recommended_prompts 单项（前端 camelCase 形态） */
export interface RecommendedPrompt {
  /** 用户点击后发给 AI 的完整 prompt 文案 */
  text: string
  /** 用作 icon 选择的语义类型，前端做 type → AntD icon 映射 */
  iconType: string
  /** 卡片副标题（说明 AI 提取来源，如「提取拍摄时间」） */
  subtitle: string
}

/**
 * icon_type → @ant-design/icons 组件 映射
 *
 * 已知类型：
 *   · time      → CalendarOutlined （拍摄时间）
 *   · member    → UserOutlined     （拍摄人）
 *   · watermark → TagOutlined      （水印字段相关）
 *
 * 未知类型走 FALLBACK_ICON 兜底（TagOutlined），便于后端先发新 type 前端再补映射。
 */
const ICON_TYPE_TO_ANTD: Record<string, AntdIconComponent> = {
  time: CalendarOutlined,
  member: UserOutlined,
  watermark: TagOutlined
}

const FALLBACK_ICON: AntdIconComponent = TagOutlined

export function iconTypeToAntdIcon(
  iconType: string | undefined | null
): AntdIconComponent {
  if (!iconType) return FALLBACK_ICON
  return ICON_TYPE_TO_ANTD[iconType] ?? FALLBACK_ICON
}

/**
 * 兜底推荐项 · init 返回空数组（团队没拍过照片 / 接口异常 fallback 到 loading 结束但无数据）
 * 时使用，保证欢迎页永远有可点入口。
 *
 * 2 项：按拍摄月份（提取拍摄时间）/ 按拍摄人（提取拍摄人）。
 */
export const FALLBACK_RECOMMENDED_PROMPTS: RecommendedPrompt[] = [
  {
    text: '按拍摄月份分类',
    iconType: 'time',
    subtitle: '提取拍摄时间'
  },
  {
    text: '按拍摄人分类',
    iconType: 'member',
    subtitle: '提取拍摄人'
  }
]

/**
 * 2 列网格裁剪 · 奇数项时丢最后一项，避免最后一行出现孤悬卡片
 *
 * 例：7 项 → 6 项（3 行 × 2 列）；6 项 → 6 项不动。
 */
export function trimForTwoColGrid<T>(items: T[]): T[] {
  return items.length % 2 === 1 ? items.slice(0, -1) : items
}

/**
 * AISidebar QuickPrompts 裁剪规则（仅会话窗口使用）
 *
 * 「最多取前 4 个，不足 4 个时取 2 或 0」：
 *   · 后端 ≥ 4 → 取 4（凑齐 2 行 × 2 列 / 4 行 × 1 列）
 *   · 后端 2-3 → 取 2（凑不齐 4 时降一档到 2）
 *   · 后端 0-1 → 取 0（少于 2 视为没法引导，直接不出）
 *
 * EmptyState（建夹页）不走本规则 —— 那边奇偶裁剪走 trimForTwoColGrid。
 */
export function trimForChat<T>(items: T[]): T[] {
  if (items.length >= 4) return items.slice(0, 4)
  if (items.length >= 2) return items.slice(0, 2)
  return []
}
